import * as React from "react";
import SearchPageMainComponent from "../../components/SearchPageMainComponent";
import useConstructor from "../../core/hooks/useConstructor";
import { useHubx } from "../../HubX";
import {
  FETCH_MULTIPLE_INSTALLATIONS,
  FETCH_INSTALLATIONS_IDS,
  FETCH_GROUPS_AND_USERS_FILTERED,
  DELETE_INSTALLATIONS_FROM_INSTALLATION_GROUPS,
} from "../../Fedx";
import { useSnackbar } from "notistack";
import { SelectOptions } from "../../core/models/SelectOptions";
import { searchPageType } from "../../core/constants/searchPagesOptions";
import { formatDateTime } from "../../helpers/datetime";
import { EMPTY_STRING_LABEL } from "../../core/constants/appGlobals";
import { InstallationGroupsAndUsersSearchDTO } from "../../core/models/search/InstallationGroupsAndUsersSearchDTO";
import { IInstallation, IInstallationRadix } from "../../core/contracts/IInstallation";
import { isArrayLastElement, isArrayValid } from "../../helpers/utility";
import User from "../../core/models/User";
import { SessionCache } from "../../helpers/cache";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@material-ui/core";
import { InstallationsIdsAndGroupsIds } from "../../core/models/search/InstallationsIdsAndGroupsIds";

type SearchMainProps = {
  searchType: searchPageType;
};

type searchLabelsFromType = {
  searchTitle: string;
  searchLabel: string;
  primaryLabel: string;
  primarySearchHeaders: string[];
  secondaryLabel: string;
  secondarySearchHeaders: string[];
};

type InstallationSearchDTO = {
  label: string;
  name: string;
  value: string;
};

function SearchInstallations({ searchType }: SearchMainProps) {
  const labelsFromType: searchLabelsFromType = React.useMemo(() => {
    return {
      searchTitle: "Consultar Instalações",
      searchLabel: "Nome Fantasia ou Número White da Instalação",
      primaryLabel: "Grupos de Instalação",
      primarySearchHeaders: [
        "Grupo de Instalação",
        "Organização",
        "Diretoria",
        "Criado por",
        "Data de Criação",
      ],
      secondaryLabel: "Resultados de Usuários",
      secondarySearchHeaders: [
        "Nome do Usuário",
        "E-mail",
        "Organização",
        "Grupo de Instalação",
        "Data de Cadastro",
        "Criado por",
      ],
    };
  }, []);

  const hub = useHubx();
  const toastr = useSnackbar();
  const [installationGroupSearchResult, setInstallationGroupSearchResult] = React.useState<
    (string | Date)[][]
  >([]);
  const [secondaryResult, setSecondaryResult] = React.useState<(string | void)[][]>([]);
  const [installations, setInstallations] = React.useState<InstallationSearchDTO[]>([]);
  const [isLoading, setLoading] = React.useState<boolean>(false);
  const [selectedOption, setSelectedOption] = React.useState<null | SelectOptions[]>(null);
  const [modalDeleteOpen, setModalDeleteOpen] = React.useState<boolean>(false);
  const [resultsToDelete, setResultsToDelete] = React.useState<any[]>([]);
  const [isLoadingDeleteResponse, setIsLoadingDeleteResponse] = React.useState<boolean>(false);

  const userOnCache = React.useMemo(() => {
    const StorageCache = new SessionCache();
    const user: User | null = StorageCache.get("user");
    return user;
  }, []);

  useConstructor(async () => {
    setLoading(true);
    try {
      const installationsIds: string[] = await hub.request(FETCH_INSTALLATIONS_IDS);

      const installations: IInstallation[] = await hub.request(
        FETCH_MULTIPLE_INSTALLATIONS,
        installationsIds
      );

      const installationRadix:
        | IInstallationRadix[]
        | undefined = userOnCache?.installationGroup?.flatMap(
        (customer) => customer.installationList
      );

      setInstallations(
        installations.map((installation: IInstallation) => {
          const auxInstallationRadix = installationRadix?.find(
            (x) => x.installationId === installation.installation_id
          );
          return {
            label: installation.installation_id ?? "",
            value: installation.installation_id ?? "",
            name: auxInstallationRadix?.name || installation?.name || "",
          };
        })
      );
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("SearchInstallationsPage -> error", error);
      toastr.enqueueSnackbar(
        "Ops! Ocorreu um erro ao tentar obter as informações dos grupos de instalação cadastrados!",
        { variant: "error" }
      );
    } finally {
      setLoading(false);
    }
  });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const updateSearchResults = async (installationsIds: string[] | null) => {
    if (!installationsIds) {
      setInstallationGroupSearchResult([]);
      setSecondaryResult([]);
      return;
    }

    setLoading(true);

    const APIResponseInstallations: InstallationGroupsAndUsersSearchDTO[] = await hub
      .request(FETCH_GROUPS_AND_USERS_FILTERED, installationsIds)
      .finally(() => setLoading(false));

    if (isArrayValid(APIResponseInstallations)) {
      setInstallationGroupSearchResult(
        APIResponseInstallations.map((group) => {
          return [
            group.id,
            group.name,
            group.organization,
            group.directorShip,
            group.createdUserName,
            formatDateTime(group.createdDate),
          ];
        })
      );

      setSecondaryResult(
        APIResponseInstallations.flatMap((group) => {
          return group.userList.map((user) => {
            return [
              user.id || EMPTY_STRING_LABEL,
              user?.name || EMPTY_STRING_LABEL,
              user?.email || EMPTY_STRING_LABEL,
              group.organization || EMPTY_STRING_LABEL,
              group.directorShip || EMPTY_STRING_LABEL,
              formatDateTime(user?.registrationDate) || EMPTY_STRING_LABEL,
              EMPTY_STRING_LABEL,
            ];
          });
        })
      );
    }
  };

  const handleOpenDeleteModal = (resultsToDeleteLink: any[]) => {
    setModalDeleteOpen(true);
    setResultsToDelete(resultsToDeleteLink);
  };

  const handleDeleteInstallationsFromGroups = async () => {
    setIsLoadingDeleteResponse(true);
    const installationsAndGroups: InstallationsIdsAndGroupsIds = {
      groupsIds: resultsToDelete?.map((x) => x[0]),
      installationsIds: selectedOption?.map((x) => x.value) ?? [],
    };
    try {
      await hub.request(DELETE_INSTALLATIONS_FROM_INSTALLATION_GROUPS, installationsAndGroups);
      toastr.enqueueSnackbar("Instalações excluídas dos grupos com sucesso!", {
        variant: "success",
      });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("SearchInstallations → handleDeleteUsersFromGroups -> error", error);
      toastr.enqueueSnackbar(
        "Ops! Ocorreu um erro ao tentar excluir os usuários dos grupos de instalação!",
        {
          variant: "error",
        }
      );
    } finally {
      refreshResults();
      setModalDeleteOpen(false);
      setIsLoadingDeleteResponse(false);
    }
  };

  const refreshResults = () => {
    if (selectedOption) {
      const optionsValues = selectedOption.map((x) => x.value.toLocaleLowerCase());
      const filteredInstallations = installations.reduce(function (
        filtered: string[],
        installation
      ) {
        if (optionsValues.includes(String(installation.value).toLocaleLowerCase()))
          filtered.push(installation.value);
        return filtered;
      },
      []);

      updateSearchResults(filteredInstallations);
    } else {
      updateSearchResults(null);
    }
  };

  React.useEffect(() => {
    refreshResults();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOption, installations]);

  return (
    <>
      <SearchPageMainComponent
        searchTitle={labelsFromType.searchTitle}
        searchLabel={labelsFromType.searchLabel}
        secondaryLabel={labelsFromType.secondaryLabel}
        primaryLabel={labelsFromType.primaryLabel}
        primaryResult={installationGroupSearchResult}
        primarySearchHeaders={labelsFromType.primarySearchHeaders}
        secondaryResult={secondaryResult}
        secondarySearchHeaders={labelsFromType.secondarySearchHeaders}
        loading={isLoading}
        options={installations}
        selectCustomRenderLabel={searchType}
        selectedOption={selectedOption}
        setSelectedOption={setSelectedOption}
        handleOpenDeleteModal={handleOpenDeleteModal}
        hasId={true}
      />

      {/* Confirm Delete dialog */}
      <Dialog open={modalDeleteOpen}>
        <DialogTitle>Confirmação</DialogTitle>
        <DialogContent>
          <DialogContentText>
            <span>
              Tem certeza que deseja excluir a(s) instalação(ções){" "}
              {selectedOption?.map((x) =>
                isArrayLastElement(selectedOption, x) ? x.value : x.value + ", "
              )}{" "}
              dos grupos de instalação{" "}
              {resultsToDelete.map((x) =>
                isArrayLastElement(resultsToDelete, x) ? x[1] : x[1] + ", "
              )}
              ?
            </span>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            // className={}
            disabled={isLoadingDeleteResponse}
            onClick={() => setModalDeleteOpen(false)}
          >
            Cancelar
          </Button>

          {isLoadingDeleteResponse ? (
            <CircularProgress />
          ) : (
            <Button variant="contained" onClick={() => handleDeleteInstallationsFromGroups()}>
              Confirmar
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
}

export default SearchInstallations;
